/**
 * Гібридний логічний годинник (HLC) — порядок злиття для скалярних полів.
 *
 * Задача, яку він розвʼязує: два пристрої правлять те саме поле офлайн.
 * Виграти має пізніша правка, але «пізніша» за настінним годинником —
 * ненадійно: годинник телефона може відставати на хвилини. HLC бере
 * фізичний час як основу й додає лічильник, який гарантує, що подія,
 * про яку ми вже знаємо, ніколи не виглядатиме як майбутня.
 *
 * Правило LWW із docs/04 порівнює саме ці мітки, тому формат мусить
 * сортуватися як рядок: фіксована ширина полів, старші розряди зліва.
 */
/** 15 цифр вистачає до 33658 року — і ширина не зміниться раніше. */
const TIME_WIDTH = 15;
/** Лічильник у base36: 4 знаки — 1 679 616 подій в одну мілісекунду. */
const COUNTER_WIDTH = 4;
/** Межі формату. Експортовані, бо це контракт, а не деталь реалізації. */
export const MAX_COUNTER = 36 ** COUNTER_WIDTH - 1;
export const MAX_MILLIS = 10 ** TIME_WIDTH - 1;
export class HlcOverflowError extends Error {
    constructor(reason) {
        super(`мітка HLC не вміщується у формат: ${reason}`);
        this.name = "HlcOverflowError";
    }
}
/**
 * Мітка → рядок. Тут же — єдина перевірка, що стан узагалі вміщується
 * у формат.
 *
 * Вона не церемонія. Лічильник понад межу дає пʼять знаків замість
 * чотирьох, і такий рядок сортується **нижче** за всі нормальні: `'1'`
 * менше за `'z'`. Наслідок — новіша правка тихо програє старішій
 * назавжди, і жодна перевірка цього не показує, бо байти цілі й запис
 * на місці. Тому межа перевіряється саме тут, у єдиному місці, де стан
 * стає байтами.
 */
export function formatHlc(state, deviceId) {
    if (state.counter > MAX_COUNTER || state.counter < 0) {
        throw new HlcOverflowError(`лічильник ${state.counter}`);
    }
    if (state.millis > MAX_MILLIS || state.millis < 0) {
        throw new HlcOverflowError(`час ${state.millis} мс`);
    }
    const t = String(state.millis).padStart(TIME_WIDTH, "0");
    const c = state.counter.toString(36).padStart(COUNTER_WIDTH, "0");
    // Ідентифікатор пристрою в кінці — розвʼязує нічию детерміновано, щоб
    // два пристрої дійшли того самого висновку без обміну повідомленнями.
    return `${t}-${c}-${deviceId}`;
}
export function parseHlc(hlc) {
    const m = /^(\d{15})-([0-9a-z]{4})-(.+)$/.exec(hlc);
    if (!m)
        return null;
    return {
        millis: Number.parseInt(m[1] ?? "", 10),
        counter: Number.parseInt(m[2] ?? "", 36),
        deviceId: m[3] ?? "",
    };
}
/**
 * Локальна подія: щось змінилося на цьому пристрої.
 *
 * Якщо фізичний час не зрушив (або пішов назад), росте лічильник. Мітка
 * ніколи не зменшується — інакше власна свіжа правка програла б власній
 * старій після переведення годинника.
 */
export function tickHlc(state, physicalMs) {
    if (physicalMs > state.millis)
        return { millis: physicalMs, counter: 0 };
    const counter = state.counter + 1;
    // Кидає, на відміну від `receiveHlc` нижче, і ця асиметрія навмисна:
    // мільйон із гаком власних подій за одну мілісекунду — це наша власна
    // помилка, і вона має бути гучною. Те саме від чужого пристрою —
    // не привід валити синхронізацію.
    if (counter > MAX_COUNTER)
        throw new HlcOverflowError(`лічильник ${counter}`);
    return { millis: state.millis, counter };
}
/**
 * Отримано подію з іншого пристрою. Наш годинник підтягується так, щоб
 * наступна власна мітка була свідомо пізнішою за чужу.
 *
 * **Функція тотальна: вона не кидає ніколи.** Вхід сюди приходить із
 * мережі, тобто від коду, якому ми не керуємо, і виняток тут зупинив би
 * синхронізацію цілком через одну зіпсовану мітку.
 *
 * Тому переповнення лічильника не помилка, а перенесення в мілісекунди.
 * Прецедент у цьому ж каталозі: `makeUuidV7` позичає наступну
 * мілісекунду з тієї самої причини — ідентифікатор «з майбутнього» на
 * одну мілісекунду кращий за два однакових.
 */
export function receiveHlc(state, remote, physicalMs) {
    const millis = Math.max(state.millis, remote.millis, physicalMs);
    const counter = millis === state.millis && millis === remote.millis
        ? Math.max(state.counter, remote.counter) + 1
        : millis === state.millis
            ? state.counter + 1
            : millis === remote.millis
                ? remote.counter + 1
                : 0;
    return counter > MAX_COUNTER ? { millis: millis + 1, counter: 0 } : { millis, counter };
}
/**
 * Годинник як обʼєкт: стан живе всередині, назовні — лише мітки.
 *
 * `initial` дає підняти годинник із того місця, де він спинився минулого
 * разу. Без нього стан починається з нуля при кожному запуску вкладки,
 * і перший `next()` бере настінний час — що майже завжди правильно
 * й ламається рівно тоді, коли годинник пристрою пішов назад.
 */
export function makeHlcClock(deviceId, now = Date.now, initial = { millis: 0, counter: 0 }) {
    let state = initial;
    return {
        next() {
            state = tickHlc(state, now());
            return formatHlc(state, deviceId);
        },
        receive(remoteHlc) {
            const remote = parseHlc(remoteHlc);
            if (!remote)
                return;
            state = receiveHlc(state, remote, now());
        },
        get state() {
            return state;
        },
    };
}
//# sourceMappingURL=hlc.js.map